import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { patternToEmployeeSchedule } from '@/helpers/BuildWorkingPattern';
import { ScheduleEditForm } from '@/pages/team/teamMember/overview/ScheduleEditForm';
import {
  useEmployeeIdWorkingPatternsQuery,
  useGetMeQuery,
  WorkingPatternPeriods
} from '@/services/gql/graphql.generated';
import { format } from 'date-fns';
import { FC, Suspense } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiEdit } from 'react-icons/fi';
import strings from '../ess.translations';

interface MyScheduleEditProps {}

const MyScheduleEdit: FC<MyScheduleEditProps> = () => {
  const [{ data: userData, error: userError }] = useGetMeQuery();
  const handleError = useErrorHandler();
  const employeeId = userData?.myEmployee?.identityId;

  const [{ data: scheduleData, error: scheduleError }] = useEmployeeIdWorkingPatternsQuery({
    variables: {
      dateAndPeriods: {
        date: format(new Date(), 'yyyy-MM-dd'),
        periods: 7
      },
      id: employeeId!
    },
    requestPolicy: 'network-only',
    pause: !employeeId
  });

  if (userError || scheduleError) {
    handleError(userError || scheduleError);
  }

  if (!userData || !userData.myEmployee) {
    throw new Error('User data is required');
  }

  const breadcrumbs: NavPage[] = [
    { name: strings.mySchedule.header, to: `/ess/my-schedule` },
    { name: strings.mySchedule.editLabel, to: `/ess/my-schedule/edit` }
  ];

  return (
    <PageContainer>
      <PageHeader icon={<FiEdit />} pageTitle={strings.mySchedule.editLabel} breadcrumbs={breadcrumbs} />
      <PageSizer mode={SizerMode.PAGE}>
        <SubmissionErrorBoundary>
          <Suspense fallback={<TableLoader />}>
            <ScheduleEditForm
              userId={employeeId!}
              userFirstName={userData.myEmployee?.firstName}
              contractTypeId={userData.myEmployee?.contract?.contractTypeId}
              initSchedule={patternToEmployeeSchedule(scheduleData?.employeeIdWorkingPatterns as WorkingPatternPeriods)}
            />
          </Suspense>
        </SubmissionErrorBoundary>
      </PageSizer>
    </PageContainer>
  );
};

export default MyScheduleEdit;
